import React, { useState, useEffect, useRef } from 'react';
import _uniqueId from 'lodash/uniqueId';
import _merge from 'lodash/merge';
import { getContext, useCombinedRefs } from 'common';
import Elevation, { getElevationStyle, ElevationProps } from 'components/Elevation';
import { handleKeyDown, handleFocus, CONSTANTS } from 'common/ListKeyboardNav';

import Styled from './styles';
import ListDivider from './ListDivider';
import ListItemBasic from './ListItemBasic';
import ListItemProgressIndicator from './ListItemProgressIndicator';
import ListGroupHeading from './ListGroupHeading';
import ListItemText from './ListItemText';
import ListItemIcon from './ListItemIcon';
import ListItemCascading from './ListItemCascading';
import CascadingList from './CascadingList';
import ListItemChecked from './ListItemChecked';

interface ListContextInterface {
  theme?: any;
  stickyHeader?: boolean;
  truncateListText?: boolean;
  uniqueListId?: string;
  hoverEffect?: boolean;
  variant?: 'outlined' | 'underlined';
}

const ListContext = React.createContext<ListContextInterface | null>({});
const { LIST_WRAPPER } = CONSTANTS;
const ELEVATION_INDEX = 8;
const ELEVATION_DARK = false;
const ELEVATION_STYLE = {
  display: 'flex',
  flexDirection: 'column' as const,
  maxHeight: '100vh',
  height: '100%'
};

export interface ListProps extends React.ComponentPropsWithRef<'div'> {
  /** Controls presence of hover effects on list items. */
  hoverEffect?: boolean;
  /** The content of the list, consider using `ListItemBasic`, `ListItemText`, `ListItemIcon`, `ListItemDivider`, and`ListGroupHeading`  */
  children: React.ReactNode;
  /** Enable/Disable keyboard navigation */
  keyboardNavigation?: boolean;
  /** Enable/Disable component focus handler when keyboardNavigation = false. */
  keyboardFocus?: boolean;
  /** Enable/Disable component focus handler when keyboardNavigation = true. */
  preventFocusHandling?: boolean;
  /** Object indicating styles to be used for the list. Most useful for setting width and maxHeight. */
  style?: React.CSSProperties;
  /** Styles and any other property for the `ul` element of list. */
  ulProps?: React.ComponentPropsWithRef<'ul'>;
  /** If `true`, list header(s) will remain 'sticky' (positioned at the top) as content is scrolled. */
  stickyHeader?: boolean;
  /** If `true`, the text in the list will be truncated with ellipsis if it exceeds the container width. Otherwise the list will have
   * a horizontal scroll bar. */
  truncateListText?: boolean;
  /** If `true`, allow overflow for list; set to false when using virtualized lists. */
  isOverflowNecessary?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Override the `Elevation` properties. See `Elevation` component for details. */
  elevationProps?: Partial<ElevationProps>;
  /** @ignore If `true`, add `Elevation` styles to the list. Otherwise the Elevation class (extra <div> element) is added to the html structure. */
  useElevationStyle?: boolean;
  /** Use scrollIntoView to scroll the List when focused. */
  scrollIntoViewOnFocus?: boolean;
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
}

/** Component for displaying a vertical list of items. All other props will be passed to the wrapper. */
const List = React.forwardRef<HTMLElement, ListProps>((props: ListProps, ref) => getContext(({ RTL, theme }) => {
  const {
    hoverEffect,
    keyboardNavigation,
    keyboardFocus,
    style: propsStyle,
    children,
    onClick,
    onBlur,
    isOverflowNecessary,
    truncateListText,
    ulProps,
    stickyHeader,
    theme: propTheme,
    elevationProps,
    useElevationStyle,
    scrollIntoViewOnFocus,
    ...otherProps
  } = props;
  const {
    style: ulStyle,
    ...otherUlProps
  } = ulProps;
  const [uniqueListId, setUniqueListId] = useState(undefined);
  const [wasTabPressed, setWasTabPressed] = useState(false);
  const listRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, listRef);

  useEffect(() => {
    setUniqueListId(_uniqueId('list-unique-identifier'));
  }, []); // on mount only

  // Callback function to keep track of when Tab was pressed. Use this to set
  // the tabIndex of the list box. If not, the list box will get focus when the
  // tab key is pressed while focused on any list items.
  const tabPressed = (val) => {
    setWasTabPressed(val);
  };

  // Need this function to reset the tabpressed state or the list box will be left with tabIndex -1.
  const handleBlur = (event) => {
    setWasTabPressed(false);
    onBlur && onBlur(event);
  };

  // used to pass on any onClick event
  const handleClick = (event) => {
    onClick && onClick(event);
  };

  // List background color overrides for AA contrast
  const backgroundColor = elevationProps.dark ? theme.ELEVATIONS[1].darkBackgroundColor : theme.ELEVATIONS[1].lightBackgroundColor;
  const resolvedStyle = _merge({}, {
    overflowX: truncateListText ? 'hidden' as const : undefined,
    backgroundColor: (useElevationStyle || !elevationProps.allowElevationBackgroundColorOverride) ? undefined : backgroundColor,
  }, propsStyle);
  const resolvedWithElevationStyle = _merge({}, {
    ...getElevationStyle(elevationProps)
  }, resolvedStyle);
  const resolvedUlStyle = _merge({}, {
    display: truncateListText ? 'block' : undefined,
  }, ulStyle);

  const userTheme = propTheme.LIST && propTheme.LIST.VERTICAL;
  const resolvedTheme = _merge({}, theme.LIST.VERTICAL, userTheme);
  const { width: listWidth, maxWidth: listMaxWidth, height: listHeight, maxHeight: listMaxHeight } = propsStyle || {};

  const listContent = (
    <Styled.List
      className={LIST_WRAPPER}
      ref={combinedRef}
      RTL={RTL}
      $theme={resolvedTheme}
      style={useElevationStyle ? resolvedStyle : resolvedWithElevationStyle}
      tabIndex={wasTabPressed ? -1 : 0}
      isOverflowNecessary={isOverflowNecessary}
      {...otherProps}
      onKeyDown={handleKeyDown(props, combinedRef, scrollIntoViewOnFocus, tabPressed, uniqueListId)}
      onClick={handleClick}
      onFocus={handleFocus(props, combinedRef, wasTabPressed, tabPressed, uniqueListId)}
      onBlur={handleBlur}
    >
      <Styled.ListUnOrdered {...otherUlProps} style={resolvedUlStyle} >
        <ListContext.Provider value={{ hoverEffect, uniqueListId, stickyHeader, truncateListText, theme: resolvedTheme }}>
          {children}
        </ListContext.Provider>
      </Styled.ListUnOrdered>
    </Styled.List>
  );

  const contentWElevation = (
    <Elevation
      style={{
        ...ELEVATION_STYLE, width: listWidth, maxWidth: listMaxWidth, height: listHeight, maxHeight: listMaxHeight,
      }} // to pick up width from List styles
      elevationIndex={elevationProps.elevationIndex}
      dark={elevationProps.dark}
      {...elevationProps}
    >
      {listContent}
    </Elevation>
  );

  return useElevationStyle ? contentWElevation : listContent;
}));

List.displayName = 'List';

List.defaultProps = {
  hoverEffect: true,
  keyboardNavigation: true,
  keyboardFocus: true,
  preventFocusHandling: false,
  style: undefined,
  ulProps: {},
  isOverflowNecessary: true,
  stickyHeader: false,
  truncateListText: false,
  theme: {},
  elevationProps: {
    elevationIndex: ELEVATION_INDEX,
    dark: ELEVATION_DARK,
    allowElevationBackgroundColorOverride: true,
  },
  useElevationStyle: false,
  scrollIntoViewOnFocus: true,
  onClick: undefined,
  onBlur: undefined
};

export {
  List as default,
  ListDivider,
  ListGroupHeading,
  ListItemBasic,
  ListItemProgressIndicator,
  ListItemIcon,
  ListItemText,
  ListItemCascading,
  ListItemChecked,
  CascadingList,
  ListContext,
};
